import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { getZendeskTickets } from '../services/api'
import ContactSupportModal from './ContactSupportModal'
import AccountMergeModal from './AccountMergeModal'
import { useAuth } from './auth/AuthProvider'

const TicketsContainer = styled.div`
  background: ${theme.color.background.light};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
`

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 2rem;
`

const TicketList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const TicketItem = styled.div`
  background: white;
  border: 1px solid ${theme.color.border.light};
  border-radius: 4px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px ${theme.color.shadow.light};
`

const TicketLink = styled.a`
  color: ${theme.color.accent.primary};
  text-decoration: none;
  font-weight: 600;
  font-size: 1.6rem;
  
  &:hover {
    text-decoration: underline;
  }
`

const TicketStatus = styled.span<{ status: string }>`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 1.2rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-top: 0.5rem;
  
  background: ${props => {
    switch (props.status.toLowerCase()) {
      case 'new': return '#e3f2fd';
      case 'open': return '#fff3e0';
      case 'pending': return '#fce4ec';
      case 'hold': return '#f3e5f5';
      case 'closed': return '#e8f5e8';
      case 'solved': return '#e8f5e8';
      default: return '#f5f5f5';
    }
  }};
  
  color: ${props => {
    switch (props.status.toLowerCase()) {
      case 'new': return '#1976d2';
      case 'open': return '#f57c00';
      case 'pending': return '#c2185b';
      case 'hold': return '#7b1fa2';
      case 'closed': return '#4caf50';
      case 'solved': return '#4caf50';
      default: return '#616161';
    }
  }};
`

const TicketDate = styled.div`
  color: ${theme.color.text.grey};
  font-size: 1.2rem;
  margin-top: 0.5rem;
`

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
`

const ErrorMessage = styled.div`
  background: #ffe6e6;
  color: #d70036;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`

const RetryButton = styled.button`
  background: #d70036;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  font-size: 1.3rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #b8002d;
  }

  &:focus {
    outline: 2px solid ${theme.color.accent.primary};
    outline-offset: 2px;
  }
`

const NoTicketsMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-style: italic;
`

const DescriptionSection = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${theme.color.border.light};
`

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: ${theme.color.accent.primary};
  cursor: pointer;
  font-size: 1.3rem;
  font-weight: 500;
  padding: 0.5rem 0;
  text-decoration: underline;
  
  &:hover {
    color: #b8002d;
  }
  
  &:focus {
    outline: 2px solid ${theme.color.accent.primary};
    outline-offset: 2px;
  }
`

const TicketDescription = styled.div`
  background: ${theme.color.background.frame};
  padding: 1rem;
  border-radius: 4px;
  margin-top: 0.5rem;
  font-size: 1.3rem;
  line-height: 1.5;
  color: ${theme.color.text.base};
  white-space: pre-wrap;
  word-wrap: break-word;
`

const CreateTicketSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: ${theme.color.background.frame};
  border-radius: 8px;
  border: 1px solid ${theme.color.border.light};
`

const CreateTicketTitle = styled.h3`
  font-size: 1.6rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin: 0 0 1rem 0;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`

const CreateTicketButton = styled.button`
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 1rem 2rem;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #b8002d;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

interface HelpTicketsProps {
  userData: any
  onTicketCountChange?: (count: number) => void
}

interface ZendeskTicket {
  id: number
  subject: string
  status: string
  created_at: string
  url: string
  description?: string
}

const HelpTickets: React.FC<HelpTicketsProps> = ({ userData, onTicketCountChange }) => {
  const { user: currentUser, isAdmin } = useAuth()
  const [tickets, setTickets] = useState<ZendeskTicket[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [expandedTickets, setExpandedTickets] = useState<Set<number>>(new Set())
  const [accountMergeModalOpen, setAccountMergeModalOpen] = useState(false)
  const [supportModalOpen, setSupportModalOpen] = useState(false)
  const [supportModalTicketType, setSupportModalTicketType] = useState<'customer' | 'employee'>('customer')

  useEffect(() => {
    if (userData?.user?.email) {
      loadHelpTickets()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.user?.email])

  const loadHelpTickets = async () => {
    if (!userData?.user?.email) return

    setIsLoading(true)
    setError('')

    try {
      const ticketData = await getZendeskTickets(userData.user.email)
      const results = ticketData.results || []
      
      // Get all tickets and map to include description
      const allTickets = results
        .filter((ticket: any) => ticket.result_type === 'ticket')
        .map((ticket: any) => ({
          id: ticket.id,
          subject: ticket.subject,
          status: ticket.status,
          created_at: ticket.created_at,
          url: ticket.url,
          description: ticket.description || ticket.raw_subject || ticket.snippet || null
        }))
      
      // Count only open tickets for the badge
      const openTicketsCount = allTickets.filter((ticket: any) => 
        ticket.status !== 'closed' && 
        ticket.status !== 'solved'
      ).length
      
      setTickets(allTickets)
      onTicketCountChange?.(openTicketsCount)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load help tickets')
      onTicketCountChange?.(0)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const toggleDescription = (ticketId: number) => {
    setExpandedTickets(prev => {
      const newSet = new Set(prev)
      if (newSet.has(ticketId)) {
        newSet.delete(ticketId)
      } else {
        newSet.add(ticketId)
      }
      return newSet
    })
  }

  const shouldShowDescription = (status: string) => {
    const normalizedStatus = status.toLowerCase()
    return normalizedStatus === 'open' || normalizedStatus === 'hold' || normalizedStatus === 'pending'
  }

  const openModal = (ticketType: 'account-merge' | 'customer' | 'employee') => {
    if (ticketType === 'account-merge') {
      setAccountMergeModalOpen(true)
    } else {
      setSupportModalTicketType(ticketType)
      setSupportModalOpen(true)
    }
  }

  const closeAccountMergeModal = () => {
    setAccountMergeModalOpen(false)
  }

  const closeSupportModal = () => {
    setSupportModalOpen(false)
  }

  if (!userData || !userData.user) {
    return null
  }

  return (
    <TicketsContainer>
      <Title>Help Tickets</Title>

      {isAdmin && <CreateTicketSection>
        <CreateTicketTitle>Create New Ticket</CreateTicketTitle>
        <ButtonGroup>
          <CreateTicketButton onClick={() => openModal('account-merge')}>
            Account Merge Request
          </CreateTicketButton>
          {/* <CreateTicketButton onClick={() => openModal('customer')}>
            Customer Ticket
          </CreateTicketButton> */}
          <CreateTicketButton onClick={() => openModal('employee')}>
            Employee Ticket
          </CreateTicketButton>
        </ButtonGroup>
      </CreateTicketSection>}

      {isLoading ? (
        <LoadingMessage>Loading help tickets...</LoadingMessage>
      ) : error ? (
        <ErrorMessage role="alert">
          <span>{error}</span>
          <RetryButton type="button" onClick={loadHelpTickets}>
            Try again
          </RetryButton>
        </ErrorMessage>
      ) : tickets.length === 0 ? (
        <NoTicketsMessage>No help tickets found</NoTicketsMessage>
      ) : (
        <TicketList>
          {tickets.map((ticket) => (
            <TicketItem key={ticket.id}>
              <TicketLink 
                href={`https://barrys.zendesk.com/agent/tickets/${ticket.id}`} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Ticket {ticket.id}: {ticket.subject}
              </TicketLink>
              <div>
                <TicketStatus status={ticket.status}>{ticket.status}</TicketStatus>
              </div>
              <TicketDate>Created: {formatDate(ticket.created_at)}</TicketDate>
              
              {shouldShowDescription(ticket.status) && ticket.description && (
                <DescriptionSection>
                  <ExpandButton 
                    onClick={() => toggleDescription(ticket.id)}
                    aria-expanded={expandedTickets.has(ticket.id)}
                  >
                    {expandedTickets.has(ticket.id) ? 'Collapse details' : 'Expand for details'}
                  </ExpandButton>
                  
                  {expandedTickets.has(ticket.id) && (
                    <TicketDescription>
                      {ticket.description}
                    </TicketDescription>
                  )}
                </DescriptionSection>
              )}
            </TicketItem>
          ))}
        </TicketList>
      )}

      <AccountMergeModal
        isOpen={accountMergeModalOpen}
        onClose={closeAccountMergeModal}
        userData={userData}
        currentUser={currentUser}
      />

      <ContactSupportModal
        isOpen={supportModalOpen}
        onClose={closeSupportModal}
        ticketType={supportModalTicketType}
        userData={userData}
        currentUser={currentUser}
      />
    </TicketsContainer>
  )
}

export default HelpTickets
